import { IFlow } from 'app/pages/Home';
import { IRequestCreateFlow } from 'services/http/flow';
import { Company } from 'types/company';
import { unMaskThousands } from 'utils/Numbers';

export interface FlowPayloadContext {
	company: Company;
	user: number;
	department: string;
	monthYear: string;
}

export const buildFlowPayload = (data: IFlow, context: FlowPayloadContext): IRequestCreateFlow => ({
	shopFlow: {
		passingShopFlow: data.passingShopFlow || 0,
		shopSellers: data.shopSellers || 0,
		customerServiceQuantity: data.shopFlowCustomerServiceQuantity || 0,
	},
	calls: {
		incomingCalls: data.incomingCalls || 0,
		attendance: unMaskThousands(data.attendance || 0),
		customerServiceQuantity: data.callsCustomerServiceQuantity || 0,
	},
	prospection: {
		prospection: data.prospection || 0,
		conversionOnFlow: String(unMaskThousands(data.conversionOnFlow || 0)),
		customerServiceQuantity: data.prospectionCustomerServiceQuantity || 0,
	},
	leads: {
		leads: data.leads || 0,
		conversionOnFlow: String(unMaskThousands(data.leadsConversionOnFlow || 0)),
		customerServiceQuantity: data.leadsCustomerServiceQuantity || 0,
	},
	workshop: {
		customerWorkshop: data.customerWorkshop || 0,
		conversionEvaluation: String(unMaskThousands(data.conversionEvaluation || 0)),
		quantity: data.quantity || 0,
	},
	vehicleProfitability: {
		averageTicket: unMaskThousands(data.averageTicket || 0),
		margin: String(unMaskThousands(data.margin || 0)),
		validGift: data.validGift || 0,
		unitGrossMargin: Number(unMaskThousands(data.unitGrossMargin || 0).toFixed(2)),
		totalMargin: unMaskThousands(data.vehicleProfitabilityTotalMargin || 0),
	},
	financing: {
		financing: data.financing || 0,
		penetrationAmount: String(unMaskThousands(data.financingPenetrationAmount || 0)),
		financedAmount: String(unMaskThousands(data.financedAmount || 0)),
		rApplied: String(unMaskThousands(data.rApplied || 0)),
		averageContractRevenue: Number(unMaskThousands(data.averageContractRevenue || 0).toFixed(2)),
		financingIncome: Number(unMaskThousands(data.financingFinancingIncome || 0).toFixed(2)),
	},
	tccb: {
		penetrationAmount: String(unMaskThousands(data.tccbPenetrationAmount || 0)),
		contracts: data.contracts || 0,
		averageContractValue: unMaskThousands(data.averageContractValue || 0),
		revenueTccb: Number(unMaskThousands(data.tccbRevenueTccb || 0).toFixed(2)),
	},
	priceOfPlacement: {
		averageSaleValue: unMaskThousands(data.averageSaleValue || 0),
		averageValueCost: unMaskThousands(data.averageValueCost || 0),
		profitDocument: Number(unMaskThousands(data.profitDocument || 0).toFixed(2)),
	},
	attentionPoints: data?.attentionPoints?.map(item => item?.point!),
	dispatcher: {
		penetrationAmount: String(unMaskThousands(data.penetrationAmount || 0)),
		averageCarRevenue: Number(unMaskThousands(data.averageCarRevenue || 0).toFixed(2)),
		dispatcherRevenue: Number(unMaskThousands(data.dispatcherRevenue || 0).toFixed(2)),
	},
	accessories: {
		unitSales: Number(unMaskThousands(data.unitSales || 0).toFixed(2)),
		saleAccessories: Number(unMaskThousands(data.accessoriesSaleAccessories || 0).toFixed(2)),
	},
	usedPickup: {
		assessmentRate: String(unMaskThousands(data.assessmentRate || 0)),
		reviewsAmount: data.reviewsAmount || 0,
		fundingRate: String(unMaskThousands(data.fundingRate || 0)),
		totalCatch: data.totalCatch || 0,
		captureAward: data.usedPickupCaptureAward || 0,
	},
	salary: {
		totalSalary: Number((data.totalSalary || 0).toFixed(2)),
		commissionOnSale: data.commissionOnSale?.toFixed(2) || '0',
		comissionOnMargin: data.comissionOnMargin?.toFixed(2) || '0',
		financingCommission: data.financingCommission?.toFixed(2) || '0',
		forwardingCommission: data.forwardingCommission?.toFixed(2) || '0',
		tccbCommission: data.tccbCommission?.toFixed(2) || '0',
		saleValue: Number((data.saleValue || 0).toFixed(2)),
		quantitySale: data.quantitySale || 0,
		totalMargin: Number((data.totalMargin || 0).toFixed(2)),
		financingIncome: Number((data.financingIncome || 0).toFixed(2)),
		dispatcherRevenue: Number(data.salaryDispatcherRevenue?.toFixed(2)) || 0,
		revenueTccb: Number((data.revenueTccb || 0).toFixed(2)),
		fundingAward: Number((data.fundingAward || 0).toFixed(2)),
		dispatcherAward: Number((data.dispatcherAward || 0).toFixed(2)),
		tccbAward: Number((data.tccbAward || 0).toFixed(2)),
		saleAccessories: Number(data.salarySaleAccessories?.toFixed(2)) || 0,
		captureAward: Number((data.salaryCaptureAward || 0).toFixed(2)),
		dsr: Number((data.dsr || 0).toFixed(2)),
		calendarDays: data.calendarDays || 0,
		sundayHolidays: data.sundayHolidays || 0,
	},
	serviceChannels: {
		passingShopFlow: {
			nome: 'Fluxo de loja passante',
			atendimentos: data.shopFlowCustomerServiceQuantity || 0,
			conversao: unMaskThousands(data.flowConversion || 0),
			vendas: data.flowSales || 0,
		},
		attendanceCalls: {
			nome: 'Comparecimento ligações',
			atendimentos: data.callsCustomerServiceQuantity || 0,
			conversao: unMaskThousands(data.callAttendanceConversion || 0),
			vendas: data.callAttendanceSales || 0,
		},
		prospection: {
			nome: 'Prospecção',
			atendimentos: data.prospectionCustomerServiceQuantity || 0,
			conversao: unMaskThousands(data.prospectingConversion || 0),
			vendas: data.prospectingSales || 0,
		},
		leads: {
			nome: 'Leads',
			atendimentos: data.leadsCustomerServiceQuantity || 0,
			conversao: unMaskThousands(data.leadsConversion || 0),
			vendas: data.leadsSales || 0,
		},
		customerWorkshop: {
			nome: 'Cliente oficina',
			atendimentos: data.quantity || 0,
			conversao: unMaskThousands(data.workshopCustomerConversion || 0),
			vendas: data.workshopCustomerSales || 0,
		},
		total: {
			nome: 'Total',
			atendimentos: data.totalCalls || 0,
			conversao: data.totalConversion || 0,
			vendas: data.totalSales || 0,
		},
	},
	company: context.company,
	user: context.user,
	department: context.department,
	monthYear: context.monthYear,
});
